/*
 * AAIKO cart page (/cart) behaviour.
 *
 * The page works without this file: quantities are updates[] in the cart form
 * and "remove" is Shopify's removal URL. This upgrades both to in-place
 * changes, adds the size swap, and keeps the page and the bag panel in step:
 * every change goes through assets/aaiko-cart-api.js, the section re-renders
 * from the same response, and the standard cart event tells the panel and the
 * header count. A change made in the panel arrives the same way and re-renders
 * this page.
 */
(function () {
  'use strict';

  var SOURCE = 'aaiko-cart-page';
  var LINE_CONTROLS = 'a[href], button, select, input';

  class AaikoCartPage extends HTMLElement {
    constructor() {
      super();
      this.busy = false;
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onCartUpdate = this.onCartUpdate.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      // The top checkout button submits the form by its `form` attribute, so
      // the submit is caught on the document rather than inside this element.
      document.addEventListener('submit', this.onSubmit);
      if (window.AaikoCart) document.addEventListener(window.AaikoCart.EVENT, this.onCartUpdate);
    }

    disconnectedCallback() {
      document.removeEventListener('submit', this.onSubmit);
      if (window.AaikoCart) document.removeEventListener(window.AaikoCart.EVENT, this.onCartUpdate);
    }

    get sectionId() {
      return this.dataset.sectionId;
    }

    onClick(event) {
      // The stepper's buttons are submit buttons (updates[key]) for the no-JS
      // path; with JS the click is taken over and nothing submits.
      var step = event.target.closest('[data-aaiko-cart-step]');
      if (step && window.AaikoCart) {
        event.preventDefault();
        if (step.disabled) return;
        this.mutate(function (sections) {
          return window.AaikoCart.change(Number(step.dataset.line), Number(step.dataset.quantity), sections);
        });
        return;
      }
      var remove = event.target.closest('[data-aaiko-cart-remove]');
      if (!remove || !window.AaikoCart) return;
      event.preventDefault();
      this.mutate(function (sections) {
        return window.AaikoCart.change(Number(remove.dataset.line), 0, sections);
      });
    }

    onChange(event) {
      if (!window.AaikoCart) return;
      var size = event.target.closest('[data-aaiko-cart-size]');
      if (size) {
        var current = size.dataset.current;
        if (!size.value || size.value === current) return;
        var options = {
          key: size.dataset.key,
          variantId: size.value,
          quantity: size.dataset.quantity,
          properties: window.AaikoCart.propertiesOf(size),
          sellingPlan: size.dataset.sellingPlan,
        };
        this.mutate(
          function (sections) {
            options.sections = sections;
            return window.AaikoCart.swapVariant(options);
          },
          function () {
            size.value = current;
          }
        );
        return;
      }
      var qty = event.target.closest('[data-aaiko-cart-qty]');
      if (!qty) return;
      this.mutate(function (sections) {
        return window.AaikoCart.change(Number(qty.dataset.line), Number(qty.value), sections);
      });
    }

    /**
     * Checkout is a real form POST. Mark the pressed button busy and swallow a
     * second submit; never `disabled`, which would drop name="checkout".
     */
    onSubmit(event) {
      var form = this.querySelector('#aaiko-cartpage-form');
      if (!form || event.target !== form) return;
      if (this.checkingOut) {
        event.preventDefault();
        return;
      }
      this.checkingOut = true;
      var button = event.submitter && event.submitter.matches('[data-aaiko-cart-checkout]') ? event.submitter : null;
      if (button) button.setAttribute('aria-busy', 'true');
      window.setTimeout(
        function () {
          this.checkingOut = false;
          if (button) button.removeAttribute('aria-busy');
        }.bind(this),
        8000
      );
    }

    onCartUpdate(event) {
      if (event && event.detail && event.detail.source === SOURCE) return;
      var settle = this.refresh.bind(this);
      if (event && event.promise && typeof event.promise.then === 'function') {
        event.promise.then(settle).catch(settle);
      } else {
        settle();
      }
    }

    /**
     * @param {(sections: string) => Promise<object>} request
     * @param {() => void} [onError]
     */
    mutate(request, onError) {
      if (this.busy) return Promise.resolve();
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      this.showError('');
      return request(this.sectionId)
        .then(
          function (data) {
            if (data && data.sections && data.sections[this.sectionId]) {
              this.render(data.sections[this.sectionId]);
            }
            window.AaikoCart.announce(SOURCE, data);
          }.bind(this)
        )
        .catch(
          function (error) {
            console.warn('[aaiko-cart-page] change failed', error);
            if (onError) onError();
            var message = (error && error.userMessage) || this.dataset.errorText || '';
            // Anything but Shopify's own refusal may have changed the bag
            // halfway (a size swap whose removal failed): re-read it.
            if (error && error.status !== 422) {
              return this.refresh().then(
                function () {
                  this.showError(message);
                }.bind(this)
              );
            }
            this.showError(message);
          }.bind(this)
        )
        .then(
          function () {
            this.busy = false;
            this.removeAttribute('aria-busy');
          }.bind(this)
        );
    }

    refresh() {
      var url = new URL(window.location.href);
      url.searchParams.set('sections', this.sectionId);
      // No `Accept: application/json`: /cart answers that header with the
      // cart JSON instead of the Section Rendering payload.
      return fetch(url.toString())
        .then(function (response) {
          return response.json();
        })
        .then(
          function (data) {
            if (data && data[this.sectionId]) this.render(data[this.sectionId]);
          }.bind(this)
        )
        .catch(function (error) {
          console.warn('[aaiko-cart-page] refresh failed', error);
        });
    }

    render(html) {
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var next = parsed.querySelector('[data-aaiko-cartpage-body]');
      var current = this.querySelector('[data-aaiko-cartpage-body]');
      if (!next || !current) return;
      // Keep the shopper's place: focus returns to the same control on the
      // same line when it still exists, otherwise to the title. Counted over
      // every control, disabled ones included, because the stepper's minus
      // switches between the two across a re-render.
      var active = document.activeElement;
      var focusLine = null;
      var focusIndex = -1;
      var hadFocus = active && this.contains(active);
      if (hadFocus) {
        var line = active.closest('[data-aaiko-cart-line]');
        if (line) {
          focusLine = line.dataset.aaikoCartLine;
          focusIndex = Array.prototype.indexOf.call(line.querySelectorAll(LINE_CONTROLS), active);
        }
      }
      current.replaceWith(next);
      if (!hadFocus) return;
      var target = null;
      if (focusLine && focusIndex >= 0) {
        var row = next.querySelector('[data-aaiko-cart-line="' + focusLine + '"]');
        target = row ? row.querySelectorAll(LINE_CONTROLS)[focusIndex] : null;
        // A minus that just became disabled hands focus to the plus beside it.
        if (target && target.disabled && target.parentElement) {
          target = target.parentElement.querySelector('button:not([disabled])');
        }
      }
      if (!target || target.disabled) {
        target = next.querySelector('.aaiko-cartpage__title');
        if (target) target.setAttribute('tabindex', '-1');
      }
      if (target) target.focus({ preventScroll: true });
    }

    /** @param {string} message empty hides the notice */
    showError(message) {
      var node = this.querySelector('[data-aaiko-cart-error]');
      if (!node) return;
      node.textContent = message;
      node.hidden = !message;
    }
  }

  if (!customElements.get('aaiko-cart-page')) {
    customElements.define('aaiko-cart-page', AaikoCartPage);
  }
})();
